import { Box, TextField, Typography } from "@mui/material";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import { EcoEfficiency } from "../../../../../models/dto/response/ResponseEcoEfficiency";
import { EconomicIndicatorComponent } from "../../../../../models/dto/response/ResponseCreateEconomicIndicators";
import { GridColDef } from "@mui/x-data-grid";
import { AppDataGrid } from "../../../../../components/datagrid/AppDataGrid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { input, economic, common } from "../../../../../i18n";
import { tableStyle } from "../../../../emission/_components/data-table/EmissionDataTableStyle";

import { textFieldEndAlignWithFloatingHelper } from "./PlanSettingPercentChangesTable";
import { NumericFormat } from "react-number-format";
import { EcoEfficiencyContext } from "../../../../../contexts/EcoEfficiencyContext";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface Props {
  editData: EcoEfficiency | undefined;
  percentChangesForm: UseFormReturn;
  rawDataForm: UseFormReturn;
  fiveYearForecast: number[];
}

export const PlanSettingRawDataTable = ({
  editData,
  rawDataForm,
  fiveYearForecast,
  percentChangesForm,
}: Props) => {
  const { t, i18n } = useTranslation([common, input, economic], {
    nsMode: "fallback",
  });

  const { calEcoEfficiencyPlans } = EcoEfficiencyContext();

  useEffect(() => {
    if (editData) {
      setRows([
        ...editData.economicindicatorsRef.components,
        { _id: "TOTAL", nameEn: "total", nameTh: "total", unit: "total" },
      ]);
    }
  }, [editData, fiveYearForecast]);

  // 🧠 ดูทุกฟิลด์ใน form
  const watchedValues = useWatch({
    control: percentChangesForm.control,
  });

  useEffect(() => {
    if (rows.length > 0) {
      setRows([...rows]);
    }
  }, [watchedValues]);

  const columns: GridColDef<EconomicIndicatorComponent>[] = [
    {
      field: "id",
      headerName: t("ID"),
      width: 80,
      sortable: false,
      filterable: false,
      colSpan: (params, row) => (row._id === "TOTAL" ? 2 : 1),
      renderCell: (params) => {
        return params.row._id !== "TOTAL"
          ? `EI-${params.api.getRowIndexRelativeToVisibleRows(params.id) + 1}`
          : `${t("Emission")} (kgCO₂e)`;
      },
    },
    {
      field: "nameEn",
      headerName: t("Components"),
      headerAlign: "left",
      minWidth: 260,
      flex: 1,
      sortable: false,
      valueGetter: (value, row) =>
        i18n.language === "th" ? row.nameTh : row.nameEn,
    },
    {
      field: "baseYear",
      headerName: t("Base-Year"),
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      sortable: false,
      valueGetter: (value, row) => editData?.baseYear ?? "-",
    },
    ...fiveYearForecast.map((y) => {
      return {
        field: `${y}`,
        headerName: `${y}`,
        headerAlign: "center",
        align: "left",
        disableColumnMenu: true,
        minWidth: 140,
        editable: false,
        sortable: false,
        renderCell: (params) => {
          const key = `${
            params.row._id === "TOTAL" ? "total" : params.row._id
          }-${y}-forecast`;

          const activated = editData?.isBaseYearComplete
            ? watchedValues[`${params.row._id}-activated`]
            : false;

          return (
            <Box
              sx={{
                ...textFieldEndAlignWithFloatingHelper,
                "& img": {
                  display: "none !important",
                },
                ".MuiFormHelperText-root": {
                  position: "absolute",
                  bottom: "-14px",
                },
              }}
            >
              <Controller
                name={key}
                key={key}
                control={rawDataForm.control}
                render={({ field }) => (
                  <NumericFormat
                    name={key}
                    key={key}
                    customInput={TextField}
                    thousandSeparator=","
                    decimalScale={2}
                    allowNegative={true}
                    allowLeadingZeros={false}
                    fixedDecimalScale
                    value={field.value ?? ""}
                    onValueChange={(values) => {
                      field.onChange(values.floatValue ?? null);
                    }}
                    onBlur={() => {
                      if (!editData?.isBaseYearComplete) return;

                      const value = rawDataForm.getValues(key);
                      if (value !== "" && value !== undefined) {
                        calEcoEfficiencyPlans(rows, fiveYearForecast);
                      }
                    }}
                    placeholder="0.00"
                    fullWidth
                    disabled={activated ?? true}
                    onKeyDown={(event) => {
                      event.stopPropagation();
                    }}
                    onKeyUp={async (event: React.KeyboardEvent) => {
                      switch (event.key) {
                        case "Enter":
                          (document.activeElement as HTMLElement).blur();
                          break;
                        default:
                          break;
                      }
                    }}
                  />
                )}
              />
              {/* <InputReactHookForm
                name={key}
                control={rawDataForm.control}
                label={""}
                placeholder={""}
                showLabelInput={false}
                rules={{
                  required: t("Required"),
                  pattern: {
                    value: NUMERIC_WITH_COMMA_REGEX,
                    message: t("Numbers-Only"),
                  },
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    // const value = event.target.value;
                    // rawDataForm.setValue(key, value.replaceAll(",", ""));

                    const raw = event.target.value.replace(/,/g, "");

                    // rawDataForm.setValue(key, event.target.value);

                    const formatted = NumberFormatterUtils.numberFormat(
                      Number(raw)
                    );

                    rawDataForm.setValue(key, formatted);
                  },
                }}
                defaultValue={0}
                disabled={activated ?? true}
                onKeyDown={(event) => {
                  event.stopPropagation();
                }}
                onKeyUp={async (event: React.KeyboardEvent) => {
                  switch (event.key) {
                    case "Enter":
                      (document.activeElement as HTMLElement).blur();
                      break;
                    default:
                      break;
                  }
                }}
              /> */}
            </Box>
          );
        },
      } as GridColDef<EconomicIndicatorComponent>;
    }),
  ];

  const [rows, setRows] = useState<EconomicIndicatorComponent[]>([]);

  return (
    <Box width={"100%"}>
      <Typography
        variant="body1"
        bgcolor={"var(--gray3)"}
        padding={"12px"}
        display={"flex"}
        alignItems={"center"}
        gap={"4px"}
        borderRadius={"4px"}
        marginBottom={"20px"}
        color={"var(--green)"}
      >
        <InfoOutlinedIcon color="primary" />
        {t("Complete-All-Fields")}
      </Typography>
      <AppDataGrid
        rows={rows}
        columns={columns}
        rowHeight={90}
        sx={{ ...tableStyle() }}
        getRowId={(row) => row._id}
        disableMultipleRowSelection={true}
        hideFooter
        autoHeight
      />
    </Box>
  );
};
